// A small UCUM conversion table for the units clinicians switch between in
// notes. Deliberately not a full UCUM engine. Mass and molar concentrations
// convert only per analyte, since the factor depends on its molar mass.

interface Factor {
  base: string
  // value_base = (value + offset) * factor
  factor: number
  offset?: number
}

interface FactorTable {
  readonly [unit: string]: Factor
}

const TABLE: FactorTable = {
  // mass → kg
  kg: { base: 'kg', factor: 1 },
  g: { base: 'kg', factor: 0.001 },
  mg: { base: 'kg', factor: 1e-6 },
  '[lb_av]': { base: 'kg', factor: 0.45359237 },
  '[oz_av]': { base: 'kg', factor: 0.028349523125 },
  // length → m
  m: { base: 'm', factor: 1 },
  cm: { base: 'm', factor: 0.01 },
  mm: { base: 'm', factor: 0.001 },
  '[in_us]': { base: 'm', factor: 0.0254 },
  '[in_i]': { base: 'm', factor: 0.0254 },
  '[ft_i]': { base: 'm', factor: 0.3048 },
  '[ft_us]': { base: 'm', factor: 0.3048006096 },
  // temperature → Cel
  Cel: { base: 'Cel', factor: 1 },
  '[degF]': { base: 'Cel', factor: 5 / 9, offset: -32 },
  K: { base: 'Cel', factor: 1, offset: -273.15 },
  // pressure → mm[Hg]
  'mm[Hg]': { base: 'mm[Hg]', factor: 1 },
  kPa: { base: 'mm[Hg]', factor: 7.50061683 },
  // time → s
  s: { base: 's', factor: 1 },
  min: { base: 's', factor: 60 },
  h: { base: 's', factor: 3600 },
  d: { base: 's', factor: 86400 },
  wk: { base: 's', factor: 604800 },
  // volume → L
  L: { base: 'L', factor: 1 },
  mL: { base: 'L', factor: 0.001 },
  dL: { base: 'L', factor: 0.1 },
  // {beats}/min and {breaths}/min share a dimension but are not
  // interchangeable, so they are left out and never convert.
}

export function convertQuantity(
  value: number,
  from: string | undefined,
  to: string | undefined
): number | null {
  if (!from || !to) {
    return null
  }
  if (from === to) {
    return value
  }
  const source = TABLE[from]
  const target = TABLE[to]
  if (!source || !target || source.base !== target.base) {
    return null
  }
  const base = (value + (source.offset ?? 0)) * source.factor
  return base / target.factor - (target.offset ?? 0)
}

interface AnalyteFactor {
  // Mass-concentration unit (UCUM).
  mass: string
  // Molar-concentration unit (UCUM).
  molar: string
  // value_molar = value_mass × factor
  factor: number
}

// Keyed by the observation's LOINC code.
interface AnalyteFactors {
  readonly [loinc: string]: AnalyteFactor
}

const ANALYTE_FACTORS: AnalyteFactors = {
  // Creatinine (MW 113.12): umol/L = mg/dL × 88.42
  '2160-0': { mass: 'mg/dL', molar: 'umol/L', factor: 88.42 },
}

// UCUM spells micro as `u`; some sources send µ.
function normalizeUnit(unit: string): string {
  return unit.replace(/µ|μ/g, 'u')
}

// `convertQuantity`, then the analyte's own mass ↔ molar factor when the
// generic table cannot convert.
export function convertAnalyteQuantity(
  code: string | undefined,
  value: number,
  from: string | undefined,
  to: string | undefined
): number | null {
  const generic = convertQuantity(value, from, to)
  if (generic !== null) {
    return generic
  }
  if (!code || !from || !to) {
    return null
  }
  const analyte = ANALYTE_FACTORS[code]
  if (!analyte) {
    return null
  }
  const source = normalizeUnit(from)
  const target = normalizeUnit(to)
  if (source === target) {
    return value
  }
  if (source === analyte.molar && target === analyte.mass) {
    return value / analyte.factor
  }
  if (source === analyte.mass && target === analyte.molar) {
    return value * analyte.factor
  }
  return null
}

// Rounds a converted or computed number to the given decimals, or to a
// sensible default: whole numbers from 100, one decimal from 10, else two.
export function roundConverted(value: number, precision?: number): number {
  if (precision !== undefined) {
    return Number(value.toFixed(precision))
  }
  const abs = Math.abs(value)
  const decimals = abs >= 100 ? 0 : abs >= 10 ? 1 : 2
  return Number(value.toFixed(decimals))
}
